// AnnualSimulator.js - Simulador Anual com visão de 12 meses
import { useState, useMemo, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Calendar,
  TrendingUp,
  Euro,
  User,
  Building,
  ArrowDown,
  ArrowUp,
  Gift,
  Briefcase,
} from "lucide-react";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

// Fallback estático PT 2026 — usado se o backend não responder.
const DEFAULT_WORKING_DAYS_2026 = [21, 19, 22, 21, 20, 20, 23, 21, 22, 21, 21, 20];

const AnnualSimulator = ({ result, formData }) => {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [workingDaysByMonth, setWorkingDaysByMonth] = useState(DEFAULT_WORKING_DAYS_2026);
  const [calendarSource, setCalendarSource] = useState("default_hardcoded");

  // Carrega o calendário laboral PT do ano actual (ou do simulation.year se existir)
  useEffect(() => {
    const year = parseInt(formData?.simulation_year) || new Date().getFullYear();
    const api = process.env.REACT_APP_BACKEND_URL;
    if (!api) return;
    fetch(`${api}/api/work-calendar/${year}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d && Array.isArray(d.working_days_per_month) && d.working_days_per_month.length === 12) {
          setWorkingDaysByMonth(d.working_days_per_month);
          setCalendarSource(d.source || "db");
        }
      })
      .catch(() => { /* fallback mantém-se */ });
  }, [formData?.simulation_year]);

  // Generate 12-month breakdown
  const monthlyBreakdown = useMemo(() => {
    if (!result) return [];

    const grossMonthly = result.gross_monthly || 0;
    const netMonthly = result.net_monthly || 0;
    const irsMonthly = result.irs_retention || 0;
    const ssEmployee = result.social_security_employee || 0;
    const ssCompany = result.social_security_company || 0;
    
    // Benefícios do formData (primários) ou do resultado
    const mealAllowanceDaily = formData?.meal_allowance_daily || 0;
    const mealMonths = parseInt(formData?.meal_allowance_months) || 11;
    const workingDaysAvg = parseFloat(formData?.working_days_month) || 21.7;
    // Sub. Refeição por mês: usa dias úteis REAIS do mês (quando daily rate definido).
    // Fallback para valor mensal fixo quando não há daily rate.
    const mealAllowanceAvg = mealAllowanceDaily > 0 ? mealAllowanceDaily * workingDaysAvg : (result.meal_allowance || 0);
    const healthInsurance = formData?.health_insurance_cost || result.breakdown?.health_insurance || 0;
    const ticketInfancia = formData?.ticket_infancia || 0;
    const ticketInfanciaMonths = formData?.ticket_infancia_months || 12;
    const ppr = formData?.insurance_capitalization || 0;
    const pprMonths = formData?.insurance_capitalization_months || 12;
    const kmAllowance = parseFloat(formData?.km_allowance) || 0;
    const kmMonths = parseInt(formData?.km_allowance_months) || 11;
    // Ajuda de Custo: distribuída visualmente em 12 meses (kmAllowance × kmMonths) / 12
    const kmPerMonthVisual = kmAllowance > 0 ? (kmAllowance * kmMonths) / 12 : 0;
    
    const clientRevenue = result.client_revenue || 0;
    const totalCompanyCost = result.total_company_cost || 0;
    const billableMonths = parseFloat(formData?.billable_months) || 11;
    const nonBillableMonths = Math.max(0, 12 - Math.ceil(billableMonths));

    // ====== Vista Realista (Opção B) ======
    // Backend devolve valores MÉDIOS mensais (anualizado ÷ 12).
    // Para mostrar o cash-flow realista por mês, "desamortizamos":
    //   - Receita raw (em meses faturáveis) = clientRevenue × 12 / billable
    //   - Custo raw base (sem subsídio) = totalCompanyCost − subsidios_amortizados_mensais
    //     onde subsidios_amortizados_mensais = (gross × 2 × 1.2375) / 12
    //   - Em Jun (Sub. Férias) e Nov (Sub. Natal): adiciona gross × 1.2375 ao custo
    //   - SS Empresa raw em meses normais = gross × 0.2375; em Jun/Dez adiciona gross × 0.2375
    //   - Anual = soma dos 12 meses (igual ao anual do backend)
    const subsidyAmortizedMonthly = (grossMonthly * 2 * 1.2375) / 12;
    const rawBaseCost = Math.max(0, totalCompanyCost - subsidyAmortizedMonthly);
    const rawRevenuePerBillable = billableMonths > 0 ? (clientRevenue * 12) / billableMonths : 0;
    const subsidyExtraCost = grossMonthly * 1.2375; // sub. férias/natal + SS empresa sobre o subsídio
    const rawSsCompanyBase = grossMonthly * 0.2375;
    const rawSsCompanySubsidy = grossMonthly * 0.2375;

    return MONTHS.map((month, index) => {
      const monthNum = index + 1;
      // Não-faturáveis: os últimos N meses (ex.: billable=11 → Dez = férias do colaborador)
      const isBillable = monthNum <= (12 - nonBillableMonths);
      const isSubsidyMonth = monthNum === 6 || monthNum === 11; // Junho (Férias) e Novembro (Natal)
      
      // Subsídios em Junho (Férias) e Novembro (Natal)
      const subsVacations = monthNum === 6 ? grossMonthly : 0;
      const subsChristmas = monthNum === 11 ? grossMonthly : 0;
      const subsidyAmount = subsVacations + subsChristmas;
      
      // Ticket Infância e PPR (aplicado apenas nos meses configurados)
      const monthTicketInfancia = monthNum <= ticketInfanciaMonths ? ticketInfancia : 0;
      const monthPpr = monthNum <= pprMonths ? ppr : 0;
      // Sub. Refeição: valor REAL por mês = daily × dias úteis desse mês (quando daily
      // rate definido). Fallback para valor mensal fixo quando apenas há meal_allowance.
      const monthWorkingDays = workingDaysByMonth[index] || workingDaysAvg;
      const monthMealReal = mealAllowanceDaily > 0 ? (mealAllowanceDaily * monthWorkingDays) : mealAllowanceAvg;
      const monthMealAllowance = monthNum <= mealMonths ? monthMealReal : 0;
      // Seguro Saúde: 12 meses
      const monthHealthInsurance = healthInsurance;
      // Ajuda de Custo: distribuída em 12 meses (fórmula (km × meses) / 12)
      const monthKmAllowance = kmPerMonthVisual;
      
      // Bruto total do mês (salário + subsídio se aplicável)
      const grossTotal = grossMonthly + subsidyAmount;
      
      // IRS sobre subsídio (taxa média aproximada)
      const irsSubsidy = subsidyAmount * 0.25;
      const irsTotal = irsMonthly + irsSubsidy;
      
      // SS sobre subsídio (colaborador 11%)
      const ssSubsidy = subsidyAmount * 0.11;
      const ssTotal = ssEmployee + ssSubsidy;
      
      // Líquido do mês (inclui TODOS os benefícios não tributados aplicáveis nesse mês)
      const netTotal = grossTotal - irsTotal - ssTotal + monthMealAllowance + monthHealthInsurance + monthTicketInfancia + monthPpr + monthKmAllowance;
      // Receita e custo realistas
      const monthRevenue = isBillable ? rawRevenuePerBillable : 0;
      const monthCost = rawBaseCost + (isSubsidyMonth ? subsidyExtraCost : 0);
      const monthMargin = monthRevenue - monthCost;
      const monthSsCompany = rawSsCompanyBase + (isSubsidyMonth ? rawSsCompanySubsidy : 0);

      return {
        month,
        monthNum,
        workingDays: monthWorkingDays,
        isBillable,
        isSubsidyMonth,
        // Colaborador
        grossBase: grossMonthly,
        subsVacations,
        subsChristmas,
        grossTotal,
        irs: irsTotal,
        ss: ssTotal,
        mealAllowance: monthMealAllowance,
        mealDaily: mealAllowanceDaily,
        healthInsurance: monthHealthInsurance,
        ticketInfancia: monthTicketInfancia,
        ppr: monthPpr,
        kmAllowance: monthKmAllowance,
        netMonthly: netTotal,
        // Empresa (valores realistas mensais)
        revenue: monthRevenue,
        cost: monthCost,
        margin: monthMargin,
        ssCompany: monthSsCompany,
      };
    });
  }, [result, formData, workingDaysByMonth]);

  // Annual totals
  const annualTotals = useMemo(() => {
    return monthlyBreakdown.reduce((acc, month) => ({
      grossTotal: acc.grossTotal + month.grossTotal,
      irs: acc.irs + month.irs,
      ss: acc.ss + month.ss,
      netTotal: acc.netTotal + month.netMonthly,
      revenue: acc.revenue + month.revenue,
      cost: acc.cost + month.cost,
      margin: acc.margin + month.margin,
      mealAllowance: acc.mealAllowance + month.mealAllowance,
      healthInsurance: acc.healthInsurance + month.healthInsurance,
      ticketInfancia: acc.ticketInfancia + month.ticketInfancia,
      ppr: acc.ppr + month.ppr,
      kmAllowance: acc.kmAllowance + month.kmAllowance,
    }), {
      grossTotal: 0,
      irs: 0,
      ss: 0,
      netTotal: 0,
      revenue: 0,
      cost: 0,
      margin: 0,
      mealAllowance: 0,
      healthInsurance: 0,
      ticketInfancia: 0,
      ppr: 0,
      kmAllowance: 0,
    });
  }, [monthlyBreakdown]);

  if (!result) {
    return (
      <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Simulador Anual
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Preencha os dados no Simulador e clique em &quot;Simular&quot; para ver a visão anual.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Resumo Anual */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-brand-50 to-brand-50 border-brand-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-brand-600 mb-1">
              <User className="w-4 h-4" />
              <span className="text-xs font-medium">Bruto Anual</span>
            </div>
            <div className="text-xl font-bold text-brand-800">{formatCurrency(annualTotals.grossTotal)}</div>
            <div className="text-xs text-brand-600 mt-1">14 meses incluídos</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-violet-600 mb-1">
              <Euro className="w-4 h-4" />
              <span className="text-xs font-medium">Líquido Anual</span>
            </div>
            <div className="text-xl font-bold text-violet-800">{formatCurrency(annualTotals.netTotal)}</div>
            <div className="text-xs text-violet-600 mt-1">Após impostos</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <Building className="w-4 h-4" />
              <span className="text-xs font-medium">Receita Empresa</span>
            </div>
            <div className="text-xl font-bold text-blue-800">{formatCurrency(annualTotals.revenue)}</div>
            <div className="text-xs text-blue-600 mt-1">{formData?.billableMonths || 11} meses faturáveis</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 text-amber-600 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xs font-medium">Margem Anual</span>
            </div>
            <div className="text-xl font-bold text-amber-800">{formatCurrency(annualTotals.margin)}</div>
            <div className="text-xs text-amber-600 mt-1">
              {annualTotals.revenue > 0 ? ((annualTotals.margin / annualTotals.revenue) * 100).toFixed(1) : 0}% margem
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabela Mensal - Colaborador */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <User className="w-4 h-4 text-blue-600" />
            Detalhe Mensal - Colaborador
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {(() => {
              // Detect which benefits are active (any month > 0) to show dynamic columns
              const hasMeal = monthlyBreakdown.some(r => r.mealAllowance > 0);
              const hasKm = monthlyBreakdown.some(r => r.kmAllowance > 0);
              const hasPpr = monthlyBreakdown.some(r => r.ppr > 0);
              const hasTicket = monthlyBreakdown.some(r => r.ticketInfancia > 0);
              // Seguro de Saúde: oculto da vista do colaborador (aparece apenas nos custos da empresa)
              const hasHealth = false;
              return (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold">Mês</TableHead>
                  <TableHead
                    className="text-xs font-semibold text-right"
                    title={`Dias úteis PT 2026 (Jan–Dez). Fonte: ${calendarSource === "db" ? "/admin/calendar" : "default nacional"}.`}
                  >
                    Dias Úteis
                  </TableHead>
                  <TableHead className="text-xs font-semibold text-right">Bruto Base</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Sub. Férias</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Sub. Natal</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Bruto Total</TableHead>
                  <TableHead className="text-xs font-semibold text-right text-red-600">IRS</TableHead>
                  <TableHead className="text-xs font-semibold text-right text-red-600">SS (11%)</TableHead>
                  {hasMeal && <TableHead className="text-xs font-semibold text-right text-brand-700">Sub. Alim.</TableHead>}
                  {hasKm && <TableHead className="text-xs font-semibold text-right text-brand-700">Deslocação</TableHead>}
                  {hasPpr && <TableHead className="text-xs font-semibold text-right text-brand-700">Real Vida</TableHead>}
                  {hasTicket && <TableHead className="text-xs font-semibold text-right text-brand-700">T. Infância</TableHead>}
                  {hasHealth && <TableHead className="text-xs font-semibold text-right text-brand-700">Seg. Saúde</TableHead>}
                  <TableHead className="text-xs font-semibold text-right text-brand-600">Líquido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyBreakdown.map((row, idx) => (
                  <TableRow 
                    key={idx} 
                    className={`text-xs ${row.isSubsidyMonth ? 'bg-violet-50' : ''} hover:bg-slate-50 cursor-pointer`}
                    onClick={() => setSelectedMonth(selectedMonth === idx ? null : idx)}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {row.month}
                        {row.isSubsidyMonth && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-violet-100 text-violet-700 border-violet-300">
                            <Gift className="w-2 h-2 mr-1" />
                            Subsídio
                          </Badge>
                        )}
                        {!row.isBillable && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-orange-100 text-orange-700 border-orange-300">
                            Não Fat.
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-slate-600" data-testid={`working-days-${row.monthNum}`}>{row.workingDays}d</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.grossBase)}</TableCell>
                    <TableCell className="text-right text-violet-600">{row.subsVacations > 0 ? formatCurrency(row.subsVacations) : '-'}</TableCell>
                    <TableCell className="text-right text-violet-600">{row.subsChristmas > 0 ? formatCurrency(row.subsChristmas) : '-'}</TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(row.grossTotal)}</TableCell>
                    <TableCell className="text-right text-red-600">-{formatCurrency(row.irs)}</TableCell>
                    <TableCell className="text-right text-red-600">-{formatCurrency(row.ss)}</TableCell>
                    {hasMeal && (
                      <TableCell
                        className="text-right text-brand-700"
                        title={row.mealAllowance > 0 && row.mealDaily > 0
                          ? `${formatCurrency(row.mealDaily)}/dia × ${row.workingDays} dias úteis = ${formatCurrency(row.mealAllowance)}`
                          : undefined}
                      >
                        {row.mealAllowance > 0 ? formatCurrency(row.mealAllowance) : '-'}
                      </TableCell>
                    )}
                    {hasKm && <TableCell className="text-right text-brand-700">{row.kmAllowance > 0 ? formatCurrency(row.kmAllowance) : '-'}</TableCell>}
                    {hasPpr && <TableCell className="text-right text-brand-700">{row.ppr > 0 ? formatCurrency(row.ppr) : '-'}</TableCell>}
                    {hasTicket && <TableCell className="text-right text-brand-700">{row.ticketInfancia > 0 ? formatCurrency(row.ticketInfancia) : '-'}</TableCell>}
                    {hasHealth && <TableCell className="text-right text-brand-700">{row.healthInsurance > 0 ? formatCurrency(row.healthInsurance) : '-'}</TableCell>}
                    <TableCell className="text-right font-bold text-brand-600">{formatCurrency(row.netMonthly)}</TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-slate-100 font-semibold border-t-2">
                  <TableCell>TOTAL ANUAL</TableCell>
                  <TableCell className="text-right text-slate-700">
                    {monthlyBreakdown.reduce((s, r) => s + (r.workingDays || 0), 0)}d
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(result.gross_monthly * 12)}</TableCell>
                  <TableCell className="text-right text-violet-600">{formatCurrency(result.gross_monthly)}</TableCell>
                  <TableCell className="text-right text-violet-600">{formatCurrency(result.gross_monthly)}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(annualTotals.grossTotal)}</TableCell>
                  <TableCell className="text-right text-red-600">-{formatCurrency(annualTotals.irs)}</TableCell>
                  <TableCell className="text-right text-red-600">-{formatCurrency(annualTotals.ss)}</TableCell>
                  {hasMeal && <TableCell className="text-right text-brand-700">{formatCurrency(annualTotals.mealAllowance)}</TableCell>}
                  {hasKm && <TableCell className="text-right text-brand-700">{formatCurrency(annualTotals.kmAllowance)}</TableCell>}
                  {hasPpr && <TableCell className="text-right text-brand-700">{formatCurrency(annualTotals.ppr)}</TableCell>}
                  {hasTicket && <TableCell className="text-right text-brand-700">{formatCurrency(annualTotals.ticketInfancia)}</TableCell>}
                  {hasHealth && <TableCell className="text-right text-brand-700">{formatCurrency(annualTotals.healthInsurance)}</TableCell>}
                  <TableCell className="text-right font-bold text-brand-600">{formatCurrency(annualTotals.netTotal)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Tabela Mensal - Empresa */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Building className="w-4 h-4 text-amber-600" />
            Detalhe Mensal - Empresa
          </CardTitle>
          <p className="text-[10px] text-muted-foreground mt-1">
            Vista realista de cash-flow: meses não faturáveis aparecem com receita 0; Junho e Novembro incluem custo do subsídio de Férias/Natal + SS Empresa. O total anual coincide com a margem anual.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold">Mês</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Receita</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Custo Total</TableHead>
                  <TableHead className="text-xs font-semibold text-right">SS Empresa</TableHead>
                  <TableHead className="text-xs font-semibold text-right text-brand-600">Margem €</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Margem %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthlyBreakdown.map((row, idx) => (
                  <TableRow 
                    key={idx} 
                    className={`text-xs ${!row.isBillable ? 'bg-orange-50' : row.isSubsidyMonth ? 'bg-violet-50' : ''}`}
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {row.month}
                        {!row.isBillable && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-orange-100 text-orange-700">
                            <ArrowDown className="w-2 h-2 mr-1" />
                            Sem receita
                          </Badge>
                        )}
                        {row.isSubsidyMonth && (
                          <Badge variant="outline" className="text-[10px] px-1 py-0 bg-violet-100 text-violet-700">
                            {row.monthNum === 6 ? 'Sub. Férias' : 'Sub. Natal'}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{formatCurrency(row.revenue)}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.cost)}</TableCell>
                    <TableCell className="text-right text-orange-600">{formatCurrency(row.ssCompany)}</TableCell>
                    <TableCell className={`text-right font-bold ${row.margin >= 0 ? 'text-brand-600' : 'text-red-600'}`}>
                      {row.margin >= 0 ? '' : '-'}{formatCurrency(Math.abs(row.margin))}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.revenue > 0 ? ((row.margin / row.revenue) * 100).toFixed(1) : '-'}%
                    </TableCell>
                  </TableRow>
                ))}
                {/* Total Row */}
                <TableRow className="bg-slate-100 font-semibold border-t-2">
                  <TableCell>TOTAL ANUAL</TableCell>
                  <TableCell className="text-right">{formatCurrency(annualTotals.revenue)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(annualTotals.cost)}</TableCell>
                  <TableCell className="text-right text-orange-600">
                    {formatCurrency(monthlyBreakdown.reduce((acc, m) => acc + m.ssCompany, 0))}
                  </TableCell>
                  <TableCell className={`text-right font-bold ${annualTotals.margin >= 0 ? 'text-brand-600' : 'text-red-600'}`}>
                    {formatCurrency(annualTotals.margin)}
                  </TableCell>
                  <TableCell className="text-right">
                    {annualTotals.revenue > 0 ? ((annualTotals.margin / annualTotals.revenue) * 100).toFixed(1) : '-'}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Resumo de Benefícios */}
      <Card className="bg-gradient-to-br from-pink-50 to-rose-50 border-pink-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Gift className="w-4 h-4 text-pink-600" />
            Benefícios Anuais
          </CardTitle>
          <p className="text-[10px] text-pink-700/70 mt-1">
            Valor oferecido ao colaborador (excl. subsídios de férias/natal e seguro de saúde).
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
            <div className="p-2 bg-white rounded border">
              <div className="text-xs text-muted-foreground">Subsídio Refeição</div>
              <div className="font-bold">{formatCurrency(annualTotals.mealAllowance)}</div>
            </div>
            <div className="p-2 bg-white rounded border">
              <div className="text-xs text-muted-foreground">Ticket Infância</div>
              <div className="font-bold">{formatCurrency(annualTotals.ticketInfancia)}</div>
            </div>
            <div className="p-2 bg-white rounded border">
              <div className="text-xs text-muted-foreground">Seguro Real Vida</div>
              <div className="font-bold">{formatCurrency(annualTotals.ppr)}</div>
            </div>
          </div>
          {/* Total Benefícios */}
          <div className="mt-3 pt-3 border-t border-pink-200 flex justify-between items-center">
            <span className="text-sm font-semibold text-pink-700">Total Benefícios Anuais</span>
            <span className="text-lg font-bold text-pink-800">
              {formatCurrency(
                annualTotals.mealAllowance + 
                annualTotals.ticketInfancia + 
                annualTotals.ppr
              )}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnnualSimulator;
